import React from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentsTab } from '@/shared/ui/components/composed/documents-tab/DocumentsTab';
import type { ClientDocument } from '../../hooks/useClientDocuments';

interface ClientDocumentsTabProps {
  documents: ClientDocument[];
  loading: boolean;
  error: string | null;
  onUpload: (file: File) => void;
  onDelete: (documentId: string) => void;
  onRename: (documentId: string, filename: string) => void;
}

/**
 * Client profile documents tab: list, upload, rename and delete attached files.
 *
 * Thin wrapper around the shared `DocumentsTab` bound to the clients i18n
 * namespace — mirrors the property documents tab.
 *
 * @param props - Documents, loading/error state and handlers
 * @returns Rendered documents tab
 */
export const ClientDocumentsTab: React.FC<ClientDocumentsTabProps> = ({
  documents,
  loading,
  error,
  onUpload,
  onDelete,
  onRename,
}) => {
  const { t } = useTranslation('features/clients');

  return (
    <DocumentsTab<ClientDocument>
      documents={documents}
      loading={loading}
      error={error}
      onUpload={onUpload}
      onDelete={onDelete}
      onRename={onRename}
      title={t('profile.documents.title')}
      errorMessage={t('profile.documents.loadError', { error })}
      emptyMessage={t('profile.documents.empty')}
      uploadLabel={t('profile.documents.upload')}
      i18nNamespace="features/clients"
      i18nKeyPrefix="profile.documents"
    />
  );
};
